import React from 'react';
import { Box, Typography } from '@heycater/design-system';
import { Grid } from '@material-ui/core';
import { ArrowBack, ArrowForward } from '@material-ui/icons';
import Head from 'next/head';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import CategoriesBanner from 'hub/components/CategoriesBanner';
import HubArticleCard from 'hub/components/HubArticleCard';
import { localeMapping } from 'lib/contentful/client';
import {
  fetchArticleCardsByCategory,
  fetchArticleCategories,
} from 'lib/contentful/hub';
import { INDEX_LOCALES } from 'shared/constants';
import { handleStaticPaths } from 'shared/helpers/handleStaticPaths';
import MainLayout from 'shared/layouts/MainLayout';
import { fetchRatings } from 'shared/services/trustedShops/fetchRatings';
import FooterLinks from 'static_pages/components/FooterLinks';
import Container from 'system/components/Container';

const ARTICLE_COLUMNS = 3;
const MAX_ARTICLE_ROWS = 7;
export const CATEGORY_ARTICLES_PER_PAGE = ARTICLE_COLUMNS * MAX_ARTICLE_ROWS;

export function HubCategory({
  articles,
  categories,
  activeCategory,
  page,
  hasNextPage,
  hasPrevPage,
  pageCount,
  ratings,
}) {
  const { t } = useTranslation('hub');
  const pages = new Array(pageCount).fill(1, 0, pageCount);

  const getPageRoute = (pageNumber) =>
    `/hub/${activeCategory.slug}/page/${pageNumber}`;

  return (
    <MainLayout ratings={ratings}>
      <Head>
        <title>Hub - {activeCategory.title} | heycater!</title>
      </Head>
      <CategoriesBanner
        categories={categories}
        activeCategory={activeCategory}
      />
      <Container size="sm">
        <Box
          as="header"
          pt={{ xs: 6, md: 9.5 }}
          pb={{ xs: 3, md: 9.5 }}
          display="flex"
          justifyContent="center"
          flexDirection="column"
        >
          <TypographyCategoryTitle
            variant="h2"
            pt={{ xs: 0, md: 3 }}
            pb={3}
            component="h1"
            align="center"
          >
            {activeCategory.title}
          </TypographyCategoryTitle>
          {activeCategory?.description && (
            <TypographyCategorySubtitle
              pb={{ xs: 2, md: 3 }}
              variant="subtitle2"
              textAlign="center"
              component="p"
            >
              {activeCategory.description}
            </TypographyCategorySubtitle>
          )}
        </Box>
      </Container>
      <Container size="md">
        <Box mb={8}>
          <Grid
            container
            direction={'row'}
            spacing={6}
            px={1.5}
            sx={{
              gridTemplateColumns: 'repeat(5, 1fr)',
            }}
          >
            {articles.map((article) => (
              <Grid key={article.slug} xs={12} md={4} item>
                <HubArticleCard {...article} key={article.hubslug} mb={3} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {pages.length > 1 && (
          <Box
            as="nav"
            aria-label="Pagination"
            mb={8}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            {hasPrevPage && (
              <Link href={getPageRoute(page - 1)} passHref>
                <PaginationButton as="a" size="md" rounded>
                  <ArrowBack />
                  <Typography fontSize="16px" ml={0.5}>
                    {t('common:labels.previous')}
                  </Typography>
                </PaginationButton>
              </Link>
            )}
            <Box mx={3}>
              {pages.map((_, pageIndex) => {
                const pageNumber = pageIndex + 1;
                return (
                  <Link
                    passHref
                    key={pageNumber}
                    href={getPageRoute(pageNumber)}
                  >
                    <PageButton as="a" active={page === pageNumber}>
                      <Typography fontSize="16px" as="span">
                        {pageNumber}
                      </Typography>
                    </PageButton>
                  </Link>
                );
              })}
            </Box>
            <div>
              {hasNextPage && (
                <Link href={getPageRoute(page + 1)} passHref>
                  <PaginationButton as="a" size="md" rounded>
                    <Typography fontSize="16px" mr={0.5}>
                      {t('common:labels.next')}
                    </Typography>
                    <ArrowForward />
                  </PaginationButton>
                </Link>
              )}
            </div>
          </Box>
        )}
      </Container>
      <FooterLinks pb={{ xs: 2, sm: 6 }} pt={{ xs: 2, sm: 3 }} />
    </MainLayout>
  );
}

const TypographyCategoryTitle = styled(Typography)`
  color: #87a34e;
  text-decoration: none;
  font-size: 48px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-size: 36px;
  }
`;

const TypographyCategorySubtitle = styled(Typography)`
  font-size: 20px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-weight: 600;
    font-size: 16px;
  }
`;

const PaginationButton = styled.button`
  text-decoration: none;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #3c4340;

  :hover {
    color: #87a34e;
  }
`;

const PageButton = styled.button`
  border: none;
  text-decoration: none;
  background: none;
  cursor: pointer;
  padding: 0.7rem 1rem;
  margin-right: 0.5rem;
  border-radius: 4px;
  color: ${(props) => (props.active ? '#87a34e' : '#3C4340')};
  font-weight: ${(props) => (props.active ? '700' : '400')};

  :hover {
    color: #87a34e;
  }
`;

export async function getStaticProps({ params, locale }) {
  const page = Number(params.page) || 1;
  const formattedLocale = localeMapping[locale];

  const [articleCards, categories, ratings] = await Promise.all([
    fetchArticleCardsByCategory({
      category: params.category,
      page: page - 1,
      pageLimit: CATEGORY_ARTICLES_PER_PAGE,
      locale: formattedLocale,
    }),
    fetchArticleCategories(formattedLocale),
    fetchRatings(),
  ]);

  const { items, total, limit, skip } = articleCards;

  const activeCategory = categories.find(
    (category) => category.slug === params.category
  );
  const pageCount = Math.ceil(total / limit);

  return {
    props: {
      articles: items,
      categories,
      activeCategory,
      page: page,
      ratings,
      hasNextPage: skip + limit < total,
      hasPrevPage: skip > 0,
      pageCount: pageCount,
    },
  };
}

export const getStaticPaths = handleStaticPaths(async () => {
  const categories = await fetchArticleCategories();

  let paths = [];
  INDEX_LOCALES.forEach((locale) => {
    paths.push(
      ...categories.map((category) => ({
        params: {
          category: category.slug,
        },
        locale,
      }))
    );
  });

  return {
    paths,
    fallback: false,
  };
});

export default HubCategory;
